import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award, Target, Star, ShieldCheck, Zap, Crown, CheckCircle, Flame } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  const [hasFired, setHasFired] = useState(false);

  const triggerBullet = () => {
    if (!hasFired) {
      setHasFired(true);
      setTimeout(() => setHasFired(false), 1500);
    }
  };

  const whyChooseUsData = [
    {
      title: "Certified & Experienced Coaches",
      desc: "Train under nationally certified NRAI coaches with years of competitive marksmanship and technical coaching experience.",
      icon: Award,
      badge: "NRAI Certified"
    },
    {
      title: "Olympic-Standard Target Systems",
      desc: "Practice on SIUS electronic targets and automated retrievers that mirror international World Cup and Olympic competition setups.",
      icon: Target,
      badge: "SIUS Telemetry"
    },
    {
      title: "100% Safety & Equipment Included",
      desc: "All safety gear, Olympic air rifles, air pistols, and precision pellets are provided with zero extra equipment fees.",
      icon: ShieldCheck,
      badge: "Zero Hazard Protocol"
    },
    {
      title: "Structured Youth & Pro Pathways",
      desc: "Customized training roadmaps for school students preparing for SGFI, State, KSS, and National Championship qualifications.",
      icon: Crown,
      badge: "National Pathway"
    }
  ];

  return (
    <section 
      id="about" 
      className="py-24 md:py-32 bg-[#050505] px-4 sm:px-6 relative overflow-hidden"
      onPointerEnter={triggerBullet}
    >
      {/* BULLET STREAK EFFECT */}
      <AnimatePresence>
        {hasFired && (
          <motion.div
            initial={{ x: "-100%", opacity: 1 }}
            animate={{ x: "200%", opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeIn" }}
            className="absolute z-[100] pointer-events-none"
            style={{ top: '35%' }}
          >
            <div className="h-[2px] w-48 bg-gradient-to-r from-transparent via-gold to-white blur-[1px]" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-[0_0_15px_#fff]" />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="max-w-7xl mx-auto space-y-24">
        
        {/* --- SECTION 1: MASTERING THE ART OF FOCUS (HERO SHOWCASE) --- */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          
          {/* Left Column: Mission & Coach Info */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }} 
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-red-600/10 border border-red-600/20 mb-6 backdrop-blur-md">
              <Zap size={14} className="text-red-500 animate-pulse" />
              <span className="text-[10px] font-black tracking-[0.4em] uppercase text-red-500">
                Mission Olympic • Sector 81 Gurugram
              </span>
            </div>

            <h2 className="font-heading text-4xl sm:text-6xl lg:text-7xl font-black text-white italic uppercase mb-6 leading-[0.85] tracking-tighter">
              MASTERING THE <br />
              <span className="text-gold">ART OF FOCUS.</span>
            </h2>

            <p className="text-zinc-400 text-sm sm:text-base leading-relaxed font-medium mb-8">
              Founded to create future Olympians, <span className="text-white font-bold italic">Chaitanya Shooting Academy</span> in Sector 81 Gurugram provides world-class air rifle and pistol training in a disciplined, high-performance environment.
            </p>

            {/* Coach Spotlight Box */}
            <div className="p-6 bg-gradient-to-br from-zinc-900 to-black border-l-4 border-red-600 rounded-r-2xl border-y border-r border-white/10 shadow-xl mb-8">
              <div className="flex items-center gap-3 mb-2">
                <Crown className="text-gold" size={22} />
                <h4 className="text-white font-heading text-xl sm:text-2xl uppercase italic font-bold">Coach Vikash Yadav</h4>
              </div>
              <p className="text-zinc-300 text-xs sm:text-sm italic mb-4">
                "Our mission is simple: To find the Olympian within every student who steps onto this range."
              </p>
              <div className="inline-block px-3 py-1 bg-gold/10 border border-gold/30 rounded text-[9px] font-black uppercase tracking-widest text-gold">
                NRAI Certified Technical Coach
              </div>
            </div>

            {/* CTA Link */}
            <div className="flex items-center gap-4">
              <Link 
                to="/book-demo"
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-red-600/30"
              >
                Schedule Free Trial
              </Link>
            </div>
          </motion.div>

          {/* Right Column: Coach Image Frame with Glow */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative rounded-[2.5rem] overflow-hidden bg-zinc-900 border-2 border-gold/30 shadow-2xl aspect-[4/5] group">
              <img 
                src="/images/coach-vikash.png" 
                alt="Head Coach Vikash Yadav - NRAI Certified Shooting Coach" 
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
                onError={(e) => {
                  e.target.src = "https://images.unsplash.com/photo-1595590424283-b8f1784cb2c8?q=80&w=2070";
                }}
              />

              {/* Top Badge */}
              <div className="absolute top-4 right-4 bg-black/80 backdrop-blur-md border border-white/20 px-3.5 py-2 rounded-xl flex items-center gap-2">
                <ShieldCheck className="text-green-500" size={16} />
                <span className="text-white font-black text-[9px] uppercase tracking-widest">NRAI Certified</span>
              </div>

              {/* Bottom Overlay Stats */}
              <div className="absolute bottom-4 left-4 right-4 p-4 sm:p-5 bg-black/90 backdrop-blur-md border border-white/10 rounded-2xl flex items-center justify-between">
                <div className="text-center flex-1">
                  <p className="text-gold font-black text-xl sm:text-2xl italic">5.0 ★</p>
                  <p className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold">Rating</p>
                </div>
                <div className="text-center flex-1 border-x border-white/10">
                  <p className="text-white font-black text-xl sm:text-2xl italic">100+</p>
                  <p className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold">Students</p>
                </div>
                <div className="text-center flex-1">
                  <p className="text-red-500 font-black text-xl sm:text-2xl italic">15</p>
                  <p className="text-[8px] uppercase tracking-widest text-zinc-400 font-bold">Lanes</p>
                </div>
              </div>
            </div>
          </motion.div>

        </div>


        {/* --- SECTION 2: WHY CHOOSE CHAITANYA SHOOTING ACADEMY (MATCHING SCREENSHOT 3) --- */}
        <div className="pt-12 border-t border-white/10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-gold/10 border border-gold/20 mb-4">
                <Flame size={14} className="text-gold" />
                <span className="text-[9px] font-black tracking-[0.35em] uppercase text-gold">The CSA Advantage</span>
              </div>

              <h3 className="font-heading text-3xl sm:text-5xl font-black text-gold italic uppercase mb-6 leading-tight tracking-tighter">
                WHY CHOOSE CHAITANYA <br />SHOOTING ACADEMY
              </h3>

              <p className="text-zinc-300 text-sm sm:text-base leading-relaxed mb-8 font-medium">
                At Chaitanya Shooting Academy, we don't just train shooters — we shape disciplined, confident athletes. With a strong focus on safety, precision, and performance, we offer a professional environment where beginners and competitive shooters can grow, improve, and excel under expert guidance.
              </p>

              {/* Feature Cards Grid */}
              <div className="grid sm:grid-cols-2 gap-4">
                {whyChooseUsData.map((item, idx) => {
                  const IconComp = item.icon;
                  return (
                    <div 
                      key={idx}
                      className="bg-zinc-900/60 border border-white/10 p-5 rounded-2xl hover:border-gold/50 transition-all group backdrop-blur-sm"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/20 flex items-center justify-center text-gold group-hover:scale-110 transition-transform">
                          <IconComp size={20} />
                        </div>
                        <span className="text-[9px] font-black uppercase text-gold tracking-wider bg-gold/10 px-2.5 py-0.5 rounded-full">
                          {item.badge}
                        </span>
                      </div>
                      <h4 className="text-white font-bold text-sm mb-1.5">{item.title}</h4>
                      <p className="text-zinc-400 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Right Media / Range Image Showcase */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-[2.5rem] overflow-hidden border border-white/10 shadow-2xl relative group bg-zinc-900">
                <img 
                  src="/images/academy-range.jpg" 
                  alt="Chaitanya Shooting Academy Electronic Range Facility" 
                  className="w-full h-[400px] sm:h-[480px] object-cover group-hover:scale-105 transition-transform duration-700"
                  onError={(e) => {
                    e.target.src = "https://images.unsplash.com/photo-1595590424283-b8f1784cb2c8?q=80&w=2070";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
                
                <div className="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-md p-5 rounded-2xl border border-white/10">
                  <p className="text-gold font-black uppercase text-xs tracking-widest mb-1">Olympic-Standard 10m Target Range</p>
                  <p className="text-white text-xs font-bold">Equipped with SIUS digital telemetry & certified coaches in Sector 81 Gurugram.</p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>

      </div>
    </section>
  );
}